import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { pipe, Option, Array as A } from 'effect';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloseIcon from '@mui/icons-material/Close';

import type { BeneficiaryItem } from '@/pages/dokkaPay/pages/configurations/company/pages/beneficiaries/components/BeneficiaryDetailCellRenderer/BeneficiaryCard';
import {
  EMPTY_TITLE,
  NICKNAME_FIELD,
  nonEmptyString,
} from '@/pages/dokkaPay/pages/configurations/company/pages/beneficiaries/components/BeneficiaryDetailCellRenderer/BeneficiaryCard';

const LIST_MAX_HEIGHT = 240;

const TITLE_ID = 'delete-default-beneficiary-title';
const LIST_LABEL_ID = 'delete-default-beneficiary-list-label';

const ACCOUNT_NUMBER_FIELDS = ['account_number', 'accountNumber'];

const accountNumber = (beneficiary: BeneficiaryItem): string =>
  pipe(
    ACCOUNT_NUMBER_FIELDS,
    A.filterMap((field) => nonEmptyString(beneficiary[field])),
    A.head,
    Option.getOrElse(() => ''),
  );

export const beneficiaryLabel = (beneficiary: BeneficiaryItem): string =>
  pipe(
    nonEmptyString(beneficiary[NICKNAME_FIELD]),
    Option.orElse(() => nonEmptyString(accountNumber(beneficiary))),
    Option.getOrElse(() => EMPTY_TITLE),
  );

const rowDetail = (beneficiary: BeneficiaryItem): string =>
  pipe(
    nonEmptyString(beneficiary[NICKNAME_FIELD]),
    Option.map(() => accountNumber(beneficiary)),
    Option.getOrElse(() => ''),
  );

interface Props {
  beneficiary: BeneficiaryItem;
  candidates: BeneficiaryItem[];
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (newDefaultId: string) => void;
}

const DeleteDefaultBeneficiaryDialog: React.FC<Props> = ({
  beneficiary,
  candidates,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const { formatMessage } = useIntl();
  const [selectedId, setSelectedId] = useState('');

  const handleSelect = useCallback((_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setSelectedId(value);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedId) onConfirm(selectedId);
  }, [selectedId, onConfirm]);

  return (
    <Dialog open disableEscapeKeyDown aria-labelledby={TITLE_ID} maxWidth="sm" fullWidth>
      <DialogTitle id={TITLE_ID} sx={{ pr: 6 }}>
        {formatMessage({
          id: 'beneficiary.delete.default.confirm.title',
          defaultMessage: 'Delete default beneficiary?',
        })}
        <IconButton
          onClick={onClose}
          size="small"
          disabled={isSubmitting}
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          title={formatMessage({ id: 'button.close', defaultMessage: 'Close' })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <FormattedMessage
            id="beneficiary.delete.default.confirm.description"
            defaultMessage="<strong>{name}</strong> is your current default beneficiary that will be deleted. This action can't be undone."
            values={{
              name: beneficiaryLabel(beneficiary),
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />{' '}
          <Box component="span" id={LIST_LABEL_ID}>
            {formatMessage({
              id: 'beneficiary.delete.default.confirm.selectPrompt',
              defaultMessage: 'Select a new default to continue:',
            })}
          </Box>
        </Typography>

        <RadioGroup
          value={selectedId}
          onChange={handleSelect}
          aria-labelledby={LIST_LABEL_ID}
          sx={{ flexWrap: 'nowrap', maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}
        >
          {candidates.map((candidate) => (
            <FormControlLabel
              key={candidate.id}
              value={candidate.id}
              control={<Radio />}
              disableTypography
              sx={{ mx: 0, py: 0.75 }}
              label={
                <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
                  <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
                    <AccountBalanceIcon fontSize="medium" />
                  </Box>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600}>
                      {beneficiaryLabel(candidate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {rowDetail(candidate)}
                    </Typography>
                  </Box>
                </Stack>
              }
            />
          ))}
        </RadioGroup>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={!selectedId || isSubmitting}>
          {formatMessage({
            id: 'beneficiary.delete.default.confirm.action',
            defaultMessage: 'Delete & Set New Default',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDefaultBeneficiaryDialog;
